// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Advisor access to a relationship's LLM-generated executive summary. Read +
 * regenerate; the summary is generated asynchronously by the intelligence-service
 * (see docs/specs/executive-summary-prd.md). Proxied by advisor-bff to the
 * resource server at /api/profiles/{profileId}/executive-summary.
 */

export type ExecutiveSummaryStatus = 'PENDING' | 'GENERATING' | 'STALE' | 'READY' | 'FAILED';

/** One action within a suggested what-if plan, in the platform's scenario vocabulary. */
export interface SuggestedWhatIfAction {
  /** ASSUMPTION | CASHFLOW_ADD | ACCOUNT_ADD | ASSET_ADD | DISPOSAL | SWEEP_SURPLUS | SWEEP_RULE */
  type?: string;
  /** Human one-line description (the LLM always supplies this). */
  label?: string;
  [key: string]: unknown;
}

export interface SuggestedWhatIfPlan {
  title?: string;
  rationale?: string;
  /** The Financial Advice Agent's note on what it checked / changed. */
  reviewNote?: string;
  actions?: SuggestedWhatIfAction[];
}

/** Reviewed what-if plans the advisor should consider (executive summary enrichment). */
export interface SuggestedWhatIfPlans {
  reviewSummary?: string;
  plans?: SuggestedWhatIfPlan[];
}

/** A single recent situation change for the Summary panel highlight. */
export interface RecentChange {
  label?: string;
  detail?: string;
}

export interface RecentChanges {
  changes?: RecentChange[];
}

export interface ExecutiveSummary {
  id?: string;
  profileId: string;
  status: ExecutiveSummaryStatus;
  /** A (re)generation is expected and no current body is shown (PENDING/GENERATING). */
  generating: boolean;
  /** A body exists but the inputs changed since it was generated. */
  stale: boolean;
  headline?: string | null;
  body?: string | null;
  /** Reviewed what-if plans to consider; null when the sub-feature produced none. */
  suggestedPlans?: SuggestedWhatIfPlans | null;
  /** Most recent material situation changes for the "What changed" highlight; null when none. */
  recentChanges?: RecentChanges | null;
  generatedAt?: string | null;
  failureReason?: string | null;
  updatedAt?: string | null;
}

export const executiveSummaryApi = {
  /** Current summary, or null when none has been generated yet (204). */
  async get(profileId: string): Promise<ExecutiveSummary | null> {
    const res = await api.get<ExecutiveSummary>(`/profiles/${profileId}/executive-summary`, {
      validateStatus: (s) => s === 200 || s === 204,
    });
    return res.status === 204 ? null : res.data;
  },

  /** Request a (re)generation; returns the summary in its in-flight state. */
  async regenerate(profileId: string): Promise<ExecutiveSummary> {
    const res = await api.post<ExecutiveSummary>(`/profiles/${profileId}/executive-summary/regenerate`);
    return res.data;
  },
};
